import { useAuth } from '../../container/authContext';
import { Initials } from '../../utility';
import React, { useMemo, useEffect, useState, useRef } from 'react';
import { getCompaniesAll, getAvailableFacilitiesForEmployee } from '../../services/company';
import useOutsideClick from '../../hook/useOutsideClick';
import { usePermission } from '../../container/permissionContext';
import { useNavigate } from 'react-router-dom';
import { isMobile, isTablet, isDesktop } from 'react-device-detect';
import { useDeviceType } from '../../hook/useDeviceType';
import { accountLabel, currentAccountLabel } from '../../utility/accounts';

export const UserMenu = ({ companyUpdated = null }) => {
  const { user, changeCompany, switchAccount } = useAuth();
  const [companiesList, setCompaniesList] = useState([]);
  const [facilitiesList, setFacilitiesList] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const wrapperRef = useRef(null);
  const { isSuperAdmin, isEmployee, isCompanySystemAdministrator } = usePermission();
  const device = useDeviceType();

  const navigation = useNavigate();
  const closeMenu = () => {
    setIsOpen(false);
  };

  const filteredCompaniesList = useMemo(() => {
    const term = companySearch.trim().toLowerCase();
    if (!term) {
      return companiesList;
    }
    return companiesList.filter((item) => item.nickname?.toLowerCase().includes(term));
  }, [companiesList, companySearch]);

  useEffect(() => {
    if (!isOpen) {
      setCompanySearch('');
    }
  }, [isOpen]);

  useOutsideClick(wrapperRef, closeMenu);

  useEffect(() => {
    if (isSuperAdmin()) {
      getCompaniesAll({ get_all: true, status: 'active' })
        .then((res) => {
          setCompaniesList(res);
        })
        .catch((e) => {
          console.log(e);
        });
    }
  }, [isSuperAdmin, companyUpdated]);

  useEffect(() => {
    if (isEmployee() || isCompanySystemAdministrator()) {
      // The person's accounts in every company: one per facility, one per corporate admin profile.
      getAvailableFacilitiesForEmployee().then((res) => {
        setFacilitiesList(res);
      });
    }
  }, [isEmployee()]);

  const toUserProfile = (e) => {
    setTimeout(() => setIsOpen(false));
    e.preventDefault();
    e.stopPropagation();
    navigation('/my-profile');
  };

  const getExtraTitle = () => {
    let title = '';

    if (user.phone == '000-000-0020') {
      title += ' (1. ';
      title += isMobile ? 'T' : 'F';
      title += isTablet ? 'T' : 'F';
      title += isDesktop ? 'T' : 'F';
      title += ' 2.';
      title += device.isMobile ? 'T' : 'F';
      title += device.isTablet ? 'T' : 'F';
      title += device.isDesktop ? 'T' : 'F';
      title += ')';
    }

    return title;
  };

  const hasSeveralAccounts = facilitiesList.length > 1;

  const switchFacility = (account) => {
    if (account.user_id === user.id) {
      setIsOpen(false);
      return;
    }

    switchAccount(account.user_id, '/corporate-library/forms').catch((e) => console.log(e));
  };

  if (user) {
    const userName = user.first_name ? `${user.first_name} ${user.last_name}` : user.username;
    const accountName = currentAccountLabel(user);

    return (
      <div className="user-menu" ref={wrapperRef}>
        <div className="user-menu-wrapper">
          {user.first_name && (
            <div className="user-logo" onClick={(e) => toUserProfile(e)}>
              <span className="avatar">{Initials(`${user.first_name} ${user.last_name}`)}</span>
            </div>
          )}
          <div className="user-name cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
            <span onClick={(e) => toUserProfile(e)} title={userName}>
              {userName} {getExtraTitle()}
            </span>
            {isSuperAdmin() && (
              <div className="user-dropdown">
                {user.company && (
                  <span className="text-secondary">
                    <span className="user-dropdown-label" title={user.company.name}>
                      {user.company.name}
                    </span>{' '}
                    <i className="icon-arrow-down"></i>
                  </span>
                )}
                {!user.company && (
                  <span className="text-secondary">
                    Select a Company <i className="icon-arrow-down"></i>
                  </span>
                )}

                {isOpen && (
                  <div className="user-dropdown-menu">
                    <div className="user-dropdown-search">
                      <input
                        type="text"
                        className="user-dropdown-search-input"
                        placeholder="Search companies..."
                        value={companySearch}
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setCompanySearch(e.target.value)}
                      />
                    </div>
                    <ul className="user-dropdown-item">
                      <li className={`${!user.company ? 'active' : ''} cursor-pointer`} onClick={() => changeCompany(null)}>
                        No company selected
                      </li>
                      {filteredCompaniesList.map((item, index) => (
                        <li
                          className={`${user.company?.id === item.id ? 'active' : ''} cursor-pointer`}
                          key={index}
                          onClick={() => changeCompany(item)}
                        >
                          {item.nickname}
                        </li>
                      ))}
                      {filteredCompaniesList.length === 0 && <li className="user-dropdown-empty">No companies found</li>}
                    </ul>
                  </div>
                )}
              </div>
            )}
            {(isEmployee() || isCompanySystemAdministrator()) && (
              <div className="user-dropdown">
                <span className="text-secondary">
                  <span className="user-dropdown-label" title={accountName}>
                    {accountName}
                  </span>{' '}
                  {hasSeveralAccounts && <i className="icon-arrow-down"></i>}
                </span>

                {isOpen && hasSeveralAccounts && (
                  <div className="user-dropdown-menu">
                    <ul className="user-dropdown-item">
                      {facilitiesList.map((account) => (
                        <li
                          className={`${account.user_id === user.id ? 'active' : ''} cursor-pointer`}
                          key={account.user_id}
                          onClick={() => switchFacility(account)}
                        >
                          {accountLabel(account)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  return null;
};
